import { useEffect, useRef, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';

import Notification from 'components/partials/notification/notification.helper';

import { clearOktaSessionInfo, OKTA_KEY } from 'helpers/okta-session';

import {
  IDLE_TIMEOUT_MS,
  IDLE_CHECK_INTERVAL_MS,
  ACTIVITY_THROTTLE_MS,
  LAST_ACTIVITY_KEY,
} from 'constants/session';

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
] as const;

const useIdleTimeout = (): void => {
  const { oktaAuth } = useOktaAuth();
  const logoutInProgressRef = useRef(false);
  const lastThrottledUpdateRef = useRef(0);

  const handleLogout = useCallback(async (): Promise<void> => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    try {
      await oktaAuth.signOut();
    } catch {
      console.error('Failed to sign out');
    }

    clearOktaSessionInfo();

    Notification.flash(
      'Session Expired',
      'info',
      'Your session has expired due to inactivity. Please log in again.',
    );

    window.location.href = '/login';
  }, [oktaAuth]);

  const isIdle = useCallback((): boolean => {
    const lastActivity = Number.parseInt(
      localStorage.getItem(LAST_ACTIVITY_KEY) ?? '',
      10,
    );

    if (!lastActivity) return false;

    return Date.now() - lastActivity > IDLE_TIMEOUT_MS;
  }, []);

  const updateActivity = useCallback((): void => {
    const now = Date.now();

    if (now - lastThrottledUpdateRef.current < ACTIVITY_THROTTLE_MS) return;

    lastThrottledUpdateRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  }, []);

  useEffect(() => {
    const storedTimestamp = localStorage.getItem(LAST_ACTIVITY_KEY);

    if (!storedTimestamp) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } else if (isIdle()) {
      handleLogout();

      return undefined;
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    const intervalId = setInterval(() => {
      if (isIdle()) handleLogout();
    }, IDLE_CHECK_INTERVAL_MS);

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        if (isIdle()) {
          handleLogout();
        } else {
          updateActivity();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === OKTA_KEY && !event.newValue) {
        clearOktaSessionInfo();
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleLogout, isIdle, updateActivity]);
};

export default useIdleTimeout;
